import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    ArrowLeft,
    Loader2,
    Calculator as CalcIcon,
    Download,
    Star as StarIcon,
    MapPin,
    Lightbulb,
    Building2,
    Share2,
} from "lucide-react";
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Area,
    AreaChart,
} from "recharts";
import CountUp from "react-countup";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import ShareDialog from "../../components/calc/ShareDialog";

const initialForm = {
    star_target: 4,
    facility_type: "city",
    city_code: "34",
    promo_strategy: "normal",
    avg_nightly_rate: 3500,
    segment_mix: { business: 30, leisure: 60, group: 10 },
};

export default function OccupancyCalculator() {
    const [form, setForm] = useState(initialForm);
    const [defaults, setDefaults] = useState(null);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shareModal, setShareModal] = useState(false);

    useSeo({
        title: "Doluluk Tahmincisi",
        description: "Otel için yıllık ve aylık doluluk tahmini, segment bazlı analiz, RevPAR ve operasyonel öneriler.",
        path: "/araclar/doluluk-tahmincisi",
    });

    useEffect(() => {
        api.get("/calculator/defaults").then(({ data }) => setDefaults(data)).catch(() => { });
    }, []);

    const update = (path, value) => {
        setForm((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const segs = path.split(".");
            let o = next;
            for (let i = 0; i < segs.length - 1; i++) o = o[segs[i]];
            o[segs[segs.length - 1]] = value;
            return next;
        });
    };

    const updateSegment = (key, value) => {
        const v = Math.max(0, Math.min(100, parseInt(value, 10) || 0));
        update(`segment_mix.${key}`, v);
    };

    const calculate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/calculator/occupancy", form);
            setResult(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    const downloadPdf = async () => {
        try {
            const res = await api.post("/calculator/occupancy/pdf", form, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "otelpro-doluluk-raporu.pdf";
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success("PDF indirildi");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const totalSegment =
        (form.segment_mix.business || 0) +
        (form.segment_mix.leisure || 0) +
        (form.segment_mix.group || 0);

    const monthlyChartData = result?.monthly_occupancy?.map((m) => ({
        name: m.month_name,
        Doluluk: m.occupancy_pct,
    })) || [];

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="occupancy-calculator-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/araclar" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tüm araçlar
                    </Link>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight inline-flex items-center gap-3">
                        <Activity className="w-8 h-8 text-gold" />
                        Doluluk Tahmincisi
                    </h1>
                    <p className="mt-3 text-white/75 max-w-2xl">
                        Lokasyon, yıldız, segment dağılımı ve fiyatlama stratejisine göre yıllık ve aylık doluluk tahmini, RevPAR analizi ve öneriler.
                    </p>
                </div>
            </section>

            <div className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-6">
                <Card title="Tesis Bilgileri">
                    <div className="space-y-5">
                        <Field label="İl / Lokasyon" Icon={MapPin}>
                            <select
                                value={form.city_code}
                                onChange={(e) => update("city_code", e.target.value)}
                                className="w-full px-3 py-2.5 rounded-md border border-slate-300 bg-white text-sm outline-none focus:border-teal"
                                data-testid="occupancy-city"
                            >
                                {(defaults?.cities || []).map((c) => (
                                    <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
                                ))}
                            </select>
                        </Field>

                        <Field label="Tesis Türü" Icon={Building2}>
                            <div className="grid grid-cols-3 gap-2">
                                {(defaults?.facility_types || []).map((f) => (
                                    <Chip
                                        key={f.value}
                                        active={form.facility_type === f.value}
                                        onClick={() => update("facility_type", f.value)}
                                        data-testid={`occupancy-facility-${f.value}`}
                                    >
                                        {f.label}
                                    </Chip>
                                ))}
                            </div>
                        </Field>

                        <Field label="Hedef Yıldız" Icon={StarIcon}>
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => update("star_target", s)}
                                        data-testid={`occupancy-star-${s}`}
                                        className={`flex-1 py-3 rounded-md border text-sm font-semibold transition ${form.star_target === s ? "bg-gold text-dark-navy border-gold" : "bg-white text-deep-blue border-slate-300 hover:border-gold/40"}`}
                                    >
                                        {Array.from({ length: s }).map(() => "★").join("")}
                                    </button>
                                ))}
                            </div>
                        </Field>

                        <Field label="Fiyat Stratejisi">
                            <div className="grid grid-cols-3 gap-2">
                                {(defaults?.promo_strategies || []).map((p) => (
                                    <Chip
                                        key={p.value}
                                        active={form.promo_strategy === p.value}
                                        onClick={() => update("promo_strategy", p.value)}
                                        data-testid={`occupancy-promo-${p.value}`}
                                    >
                                        {p.label}
                                    </Chip>
                                ))}
                            </div>
                        </Field>

                        <Field label="Ortalama Oda Fiyatı (ADR, ₺)">
                            <input
                                type="number"
                                value={form.avg_nightly_rate}
                                onChange={(e) => update("avg_nightly_rate", parseFloat(e.target.value) || 0)}
                                step={100}
                                className="w-full px-3 py-2.5 rounded-md border border-slate-300 bg-white text-sm font-data outline-none focus:border-teal"
                                data-testid="occupancy-adr"
                            />
                        </Field>

                        <Field label={`Segment Dağılımı (Toplam: %${totalSegment})`}>
                            <div className="grid grid-cols-3 gap-3">
                                <SegmentBox label="Business" value={form.segment_mix.business} onChange={(v) => updateSegment("business", v)} testId="occupancy-segment-business" />
                                <SegmentBox label="Leisure" value={form.segment_mix.leisure} onChange={(v) => updateSegment("leisure", v)} testId="occupancy-segment-leisure" />
                                <SegmentBox label="Group" value={form.segment_mix.group} onChange={(v) => updateSegment("group", v)} testId="occupancy-segment-group" />
                            </div>
                            {totalSegment !== 100 && (
                                <p className="mt-2 text-xs text-data-red font-semibold">
                                    Toplam %100 olmalı (şu an %{totalSegment}). Otomatik normalize edilir.
                                </p>
                            )}
                        </Field>

                        <button
                            onClick={calculate}
                            disabled={loading}
                            className="w-full bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm py-3 rounded-md inline-flex items-center justify-center gap-2 disabled:opacity-70 shadow-md"
                            data-testid="occupancy-calculate-btn"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalcIcon className="w-4 h-4" />}
                            Hesapla
                        </button>
                    </div>
                </Card>

                {result ? (
                    <Card title="Sonuç">
                        <div className="bg-dark-navy text-white rounded-lg p-5">
                            <span className="text-[10px] uppercase tracking-[0.2em] text-teal font-semibold">Yıllık Ortalama Doluluk</span>
                            <div className="mt-1 font-data text-4xl sm:text-5xl font-bold text-gold">
                                <CountUp end={result.annual_avg_occupancy_pct} duration={1.6} decimals={1} />
                                <span className="text-2xl text-white/80 ml-1">%</span>
                            </div>
                            <div className="mt-2 text-xs text-white/60">
                                Tip: <strong className="text-white capitalize">{result.city_type}</strong>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mt-4">
                            <ResultStat label="Yıllık RevPAR" value={result.annual_revpar} suffix=" ₺" decimals={0} accent="teal" />
                            <ResultStat label="Ağırlıklı ADR" value={result.weighted_adr} suffix=" ₺" decimals={0} accent="gold" />
                            <ResultStat label="Oda Başı Yıllık Gelir" value={result.annual_revenue_per_room} suffix=" ₺" decimals={0} accent="deep-blue" full />
                        </div>

                        <button
                            onClick={downloadPdf}
                            className="mt-4 w-full bg-deep-blue hover:bg-dark-navy text-white text-sm font-semibold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="occupancy-pdf-btn"
                        >
                            <Download className="w-4 h-4" /> PDF Rapor İndir
                        </button>
                        <button
                            onClick={() => setShareModal(true)}
                            className="mt-2 w-full bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold py-2.5 rounded-md inline-flex items-center justify-center gap-2"
                            data-testid="occupancy-share-btn"
                        >
                            <Share2 className="w-4 h-4" /> Sosyal Medyada Paylaş
                        </button>
                    </Card>
                ) : (
                    <Card title="Sonuç">
                        <div className="text-center py-12 text-deep-blue/60 text-sm">
                            <Activity className="w-10 h-10 mx-auto mb-3 text-teal/50" />
                            Doluluk tahmininizi görmek için soldaki formu doldurun.
                        </div>
                    </Card>
                )}
            </div>

            {result && (
                <div className="max-w-5xl mx-auto px-6 pb-14 space-y-6">
                    <Card title="Aylık Doluluk Tahmini">
                        <div className="h-72" data-testid="occupancy-monthly-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={monthlyChartData}>
                                    <defs>
                                        <linearGradient id="occGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#39c8c1" stopOpacity={0.85} />
                                            <stop offset="95%" stopColor="#39c8c1" stopOpacity={0.08} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#64748b" tickFormatter={(v) => `${v}%`} />
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => `%${v}`} />
                                    <Area type="monotone" dataKey="Doluluk" stroke="#39c8c1" strokeWidth={2} fill="url(#occGrad)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>

                    <Card title="Segment Dağılımı">
                        <div className="grid sm:grid-cols-3 gap-3" data-testid="occupancy-segment-grid">
                            {Object.entries(result.segment_breakdown).map(([k, v]) => (
                                <div key={k} className="rounded-md bg-light-bg p-4">
                                    <div className="text-xs uppercase tracking-wider text-deep-blue/70 font-semibold capitalize">{k}</div>
                                    <div className="mt-1 font-data text-2xl font-bold text-dark-navy">%{v.share_pct}</div>
                                    <div className="mt-2 text-xs text-deep-blue/80">ADR: <strong className="font-data">{v.adr.toLocaleString("tr-TR")} ₺</strong></div>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card title="Operasyonel Öneriler">
                        <div className="space-y-2" data-testid="occupancy-recommendations">
                            {result.recommendations.map((r, i) => (
                                <div key={i} className="flex items-start gap-3 p-3 rounded-md bg-teal/5 border-l-4 border-teal">
                                    <Lightbulb className="w-4 h-4 text-teal-700 mt-0.5 shrink-0" />
                                    <p className="text-sm text-deep-blue/85">{r}</p>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}

            {shareModal && result && (
                <ShareDialog
                    open={shareModal}
                    onClose={() => setShareModal(false)}
                    tool="occupancy"
                    summary={{
                        annual_avg_occupancy_pct: result.annual_avg_occupancy_pct,
                        annual_revpar: result.annual_revpar,
                        weighted_adr: result.weighted_adr,
                        city_code: form.city_code,
                        star: form.star_target,
                    }}
                />
            )}
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-6">
            {title && <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-4">{title}</h2>}
            {children}
        </div>
    );
}

function Field({ label, Icon, children }) {
    return (
        <div>
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider inline-flex items-center gap-1.5">
                {Icon && <Icon className="w-3.5 h-3.5 text-teal" />}
                {label}
            </span>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function Chip({ active, onClick, children, ...rest }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-2.5 rounded-md border text-sm font-semibold transition ${active ? "bg-dark-navy text-white border-dark-navy" : "bg-white text-deep-blue border-slate-300 hover:border-teal/50"}`}
            {...rest}
        >
            {children}
        </button>
    );
}

function SegmentBox({ label, value, onChange, testId }) {
    return (
        <div className="rounded-md bg-light-bg p-3">
            <div className="text-xs uppercase tracking-wider font-semibold text-deep-blue/80">{label}</div>
            <div className="mt-2 flex items-center gap-2">
                <input
                    type="number"
                    min={0}
                    max={100}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm font-data text-center"
                    data-testid={testId}
                />
                <span className="text-sm font-semibold text-deep-blue/70">%</span>
            </div>
        </div>
    );
}

function ResultStat({ label, value, suffix = "", decimals = 0, accent = "deep-blue", full }) {
    const color = accent === "gold" ? "text-gold-700" : accent === "teal" ? "text-teal-700" : "text-dark-navy";
    return (
        <div className={`rounded-md bg-light-bg p-3 ${full ? "col-span-2" : ""}`}>
            <div className="text-[10px] uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</div>
            <div className={`mt-1 font-data text-xl font-bold ${color}`}>
                <CountUp end={value} decimals={decimals} duration={1.2} separator="." />{suffix}
            </div>
        </div>
    );
}
